import { useState, useMemo, useCallback } from 'react';
import { loadFromCookie, saveJsonToCookie } from '../utils/cookieUtils';
import type { Vehicle, StatusItem, Reservation } from '../types';

const BOTTOM_TABS_COOKIE_KEY = 'daylist_bottom_tab_configs';

interface BottomTabConfig {
  id: number;
  name: string;
  selectedLabels: string[];
  includeArchived: boolean;
}

interface ParsedTabConfig {
  id?: number;
  name?: string;
  label?: string;
  selectedLabels?: string[];
  includeArchived?: boolean;
}

const DEFAULT_BOTTOM_TABS: BottomTabConfig[] = [
  { id: 0, name: '空港店', selectedLabels: ['空港店'], includeArchived: false },
  { id: 1, name: '三井P', selectedLabels: ['三井P'], includeArchived: false },
  { id: 2, name: 'ヤード', selectedLabels: ['ヤード'], includeArchived: false },
  { id: 3, name: '貸出予定', selectedLabels: ['予約確定'], includeArchived: false },
  { id: 4, name: '返却予定', selectedLabels: ['貸出中'], includeArchived: false },
];

const loadBottomTabConfigsFromCookie = (): BottomTabConfig[] => {
  const parsed = loadFromCookie(BOTTOM_TABS_COOKIE_KEY, null) as ParsedTabConfig[] | null;
  if (!parsed) return DEFAULT_BOTTOM_TABS;
  return DEFAULT_BOTTOM_TABS.map((def, idx) => {
    const existing = parsed[idx];
    if (!existing) return def;
    return {
      id: existing.id ?? idx,
      name: existing.name || existing.label || def.name,
      selectedLabels: existing.selectedLabels || (existing.label ? [existing.label] : def.selectedLabels),
      includeArchived: existing.includeArchived ?? false,
    };
  });
};

const saveBottomTabConfigsToCookie = (configs: BottomTabConfig[]): void => saveJsonToCookie(BOTTOM_TABS_COOKIE_KEY, configs, 365);

interface ToastState {
  open: boolean;
  message: string;
  severity: string;
}

interface UseBottomTabsOptions {
  vehicles: Record<string, Vehicle>;
  locationStatusList: StatusItem[];
  vehicleStatusList: StatusItem[];
  reservationStatusList: StatusItem[];
  departures: Reservation[];
  returns: Reservation[];
  onToast: (toast: ToastState) => void;
}

export function useBottomTabs({
  vehicles,
  locationStatusList,
  vehicleStatusList,
  reservationStatusList,
  departures,
  returns,
  onToast,
}: UseBottomTabsOptions) {
  const [bottomTabValue, setBottomTabValue] = useState<number>(0);
  const [bottomTabConfigs, setBottomTabConfigs] = useState<BottomTabConfig[]>(loadBottomTabConfigsFromCookie());
  const [tabConfigDialogOpen, setTabConfigDialogOpen] = useState<boolean>(false);
  const [editingTabConfigs, setEditingTabConfigs] = useState<BottomTabConfig[]>([]);

  const handleBottomTabChange = useCallback((_event: any, newValue: number) => {
    setBottomTabValue(newValue);
  }, []);

  const handleOpenTabConfig = useCallback(() => {
    setEditingTabConfigs(bottomTabConfigs.map(c => ({ ...c })));
    setTabConfigDialogOpen(true);
  }, [bottomTabConfigs]);

  const handleSaveTabConfig = useCallback(() => {
    setBottomTabConfigs(editingTabConfigs);
    saveBottomTabConfigsToCookie(editingTabConfigs);
    setTabConfigDialogOpen(false);
    onToast({ open: true, message: 'タブ設定を保存しました', severity: 'success' });
  }, [editingTabConfigs, onToast]);

  const handleTabConfigChange = useCallback((index: number, field: string, value: any) => {
    const newConfigs = [...editingTabConfigs];
    (newConfigs[index] as any)[field] = value;
    setEditingTabConfigs(newConfigs);
  }, [editingTabConfigs]);

  const locationGroupVehicles = useMemo(() => {
    const config = bottomTabConfigs[bottomTabValue];
    if (!config || !config.selectedLabels || config.selectedLabels.length === 0) return [];

    const selectedLabels = config.selectedLabels;

    const targetLocIds = locationStatusList.filter(opt => selectedLabels.includes(opt.label)).map(opt => opt.id);
    const targetStatIds = vehicleStatusList.filter(opt => selectedLabels.includes(opt.label)).map(opt => opt.id);
    const targetResIds = reservationStatusList.filter(opt => selectedLabels.includes(opt.label)).map(opt => opt.id);

    const includeArchived = config.includeArchived === true;

    return Object.values(vehicles).filter(v => {
      const isArchived = v.active === 'OFF' || (v as any).is_active === false || v.out_of_use === true;
      if (isArchived && !includeArchived) return false;

      const isLocationMatch = targetLocIds.includes(v.location_status as any) || targetStatIds.includes(v.status as any);
      if (isLocationMatch) return true;

      if (targetResIds.length > 0) {
        const hasMatchingReservation = [...departures, ...returns].some(r =>
          r.vehicle_id === v.id && targetResIds.includes(r.status as any)
        );
        if (hasMatchingReservation) return true;
      }

      return false;
    }).sort((a, b) => (a.vehicle_code || '').localeCompare(b.vehicle_code || ''));
  }, [vehicles, locationStatusList, vehicleStatusList, reservationStatusList, bottomTabValue, bottomTabConfigs, departures, returns]);

  return {
    bottomTabValue,
    setBottomTabValue,
    bottomTabConfigs,
    tabConfigDialogOpen,
    setTabConfigDialogOpen,
    editingTabConfigs,
    locationGroupVehicles,
    handleBottomTabChange,
    handleOpenTabConfig,
    handleSaveTabConfig,
    handleTabConfigChange,
  };
}
